import { useState } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Divider from "@mui/material/Divider";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import layout from "../../constants/layout";
import palette from "../../constants/palette";
import { styled } from "@mui/material/styles";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";
import { useNavContext } from "../../context/NavContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import { Dashboard } from "@mui/icons-material";

const NavBarIconWrapper = ({ icon, isCurrent }) => {
  return (
    <Box
      sx={{
        marginRight: "1rem",
        color: isCurrent ? "red" : null,
      }}
    >
      {icon}
    </Box>
  );
};

export const NAV_GROUPS_ALWAYS_EXPANDED = true;
export const ALWAYS_TEMPORARY = true;

const NavBarSectionTypography = styled("div")(({ theme }) => ({
  ...theme.typography.button,
  fontWeight: "bold",
}));

export default function NavDrawer({
  open,
  setOpen,
  navlinkSections,
  ...props
}) {
  const [expanded, setExpanded] = useState(window.location.pathname);
  const muiTheme = useTheme();
  const { isNavDisabled } = useNavContext();
  const navigate = useNavigate();

  const handleChange = (path) => (event, newExpanded) => {
    setExpanded(newExpanded ? path : "");
  };

  const toggleDrawer = (newOpen) => {
    setOpen(newOpen);
  };

  const isCurrentPath = (path) => {
    if (path == "/") {
      return window.location.pathname == "/";
    }
    const start = window.location.pathname.slice(0, path.length);
    return start === path;
  };

  const isCurrentSection = (section) => {
    if (window.location.pathname == section.path) {
      return true;
    }
    return section.links.filter((link) => isCurrentPath(link.path)).length > 0;
  };

  const DrawerList = (
    <Box
      sx={{
        width: `${layout.navDrawerWidth}rem`,
        marginTop: `${layout.navBarHeight}rem`,
        height: "100%",
        overflowY: "auto",
        backgroundColor: muiTheme.palette.background.paper,
        display: "flex",
        justifyContent: "flex-start",
        flexDirection: "column",
      }}
    >
      <Box sx={{ width: "100%" }}>
        {navlinkSections.map((section, i) => (
          <div key={i}>
            {i !== 0 && <Divider />}
            {section.isDashboard ? (
              <Button
                sx={{ p: 2, borderRadius: "none" }}
                fullWidth
                onClick={() => {
                  navigate(section.path);
                  setOpen(false);
                }}
                startIcon={
                  <Dashboard
                    sx={{
                      color:
                        window.location.pathname == section.path
                          ? "red"
                          : undefined,
                    }}
                  />
                }
              >
                <NavBarSectionTypography>Dashboard</NavBarSectionTypography>
              </Button>
            ) : (
              <Accordion
                expanded={
                  expanded.slice(0, section.path.length) === section.path ||
                  NAV_GROUPS_ALWAYS_EXPANDED
                }
                onChange={handleChange(section.path)}
                elevation={0}
                sx={{
                  borderRadius: "none",
                  padding: 0,
                  margin: 0,
                }}
                disableGutters
              >
                <AccordionSummary
                  expandIcon={
                    NAV_GROUPS_ALWAYS_EXPANDED ? null : <ExpandMoreIcon />
                  }
                  disabled={isNavDisabled}
                >
                  <NavBarIconWrapper
                    icon={section.icon}
                    isCurrent={isCurrentSection(section)}
                  />
                  <NavBarSectionTypography>
                    {section.section}
                  </NavBarSectionTypography>
                </AccordionSummary>
                <AccordionDetails>
                  <List
                    sx={{
                      height: "fit-content",
                      padding: "0",
                      borderRadius: "none",
                    }}
                  >
                    {section.links.map((navLink, i) => (
                      <ListItem key={i} disablePadding>
                        <ListItemButton
                          onClick={(e) => {
                            if (!isNavDisabled && navLink.path) {
                              if (navLink.external) {
                                window.location.assign(navLink.path);
                              } else {
                                navigate(navLink.path);
                                setOpen(false);
                              }
                            } else if (!isNavDisabled && navLink.onClick) {
                              navLink.onClick(e);
                            }
                          }}
                          sx={{
                            pointerEvents: isNavDisabled ? "none" : "auto",
                            opacity: isNavDisabled ? 0.5 : 1,
                            borderLeft: isCurrentPath(navLink.path)
                              ? `2px solid ${palette.red}`
                              : `2px solid ${palette.gray}`,

                            "&:hover": {
                              borderLeft: isNavDisabled
                                ? `2px solid ${palette.gray}`
                                : `2px solid ${palette.red}`,
                            },
                          }}
                          disabled={navLink.disabled || false}
                        >
                          <ListItemText primary={navLink.label} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}
          </div>
        ))}
      </Box>
    </Box>
  );

  if (ALWAYS_TEMPORARY || isNavDisabled) {
    props.variant = "temporary";
  }

  return (
    <Drawer open={open} onClose={() => toggleDrawer(false)} {...props}>
      {DrawerList}
    </Drawer>
  );
}
